import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import useSiteContent from "../lib/useSiteContent";
import useMobileAppRedirect, { isNativeApp } from "../lib/useMobileAppRedirect";
import { useAuth } from "../lib/auth";
import PublicNav from "../layouts/PublicNav";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { ArrowUpRight, ListChecks, ClockCountdown, ArrowRight } from "@phosphor-icons/react";
import FoundingPatronCTA from "../components/FoundingPatronCTA";
import SeoHead from "../components/SeoHead";

const HERO_BG = "https://images.unsplash.com/photo-1762279389020-eeeb69c25813?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxkYXRhJTIwdmlzdWFsaXphdGlvbiUyMGdyYXBoJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc4Mjc4ODkyfDA&ixlib=rb-4.1.0&q=85";

export default function PublicHome() {
  const [trackers, setTrackers] = useState([]);
  const [insights, setInsights] = useState([]);
  const [closedCount, setClosedCount] = useState(0);
  const [homeWaves, setHomeWaves] = useState([]);
  const c = useSiteContent();
  const { user } = useAuth();
  const nav = useNavigate();
  useMobileAppRedirect();

  useEffect(() => {
    api.get("/public/trackers").then((r) => setTrackers(r.data.trackers || []));
    // Mixed insights feed (trackers + snapshots) drives the homepage results strip.
    api.get("/insights").then((r) => {
      setInsights(r.data.items || []);
      setClosedCount(r.data.count || 0);
    }).catch(() => {});
    // ITER 123 — "Open Waves" strip: 3 latest open waves shown right under
    // the methodology strip. Admin-toggleable via Site Content →
    // home_waves_and_articles_enabled.
    api.get("/public/waves").then((r) => setHomeWaves((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  const isPatron = user && (user.role === "panelist" || user.role === "patron");
  const profileComplete = isPatron && !!user.profile?.profile_complete && (user.profile?.sectors_of_interest || []).length >= 3;

  const openWave = (w) => {
    if (!user) {
      nav(`/register?next=${encodeURIComponent("/panelist/waves")}`);
      return;
    }
    if (isPatron) {
      if (profileComplete) nav(`/panelist/survey/${w.id}`);
      else { toast.error("Complete your profile first to take surveys."); nav("/panelist/profile"); }
      return;
    }
    nav("/panelist");
  };

  // T() resolves a CMS-overridable label with fallback. Returns the override
  // even if it's an empty string — caller decides whether to hide.
  const T = (k, fallback) => (c[k] !== undefined ? c[k] : fallback);
  // showStat(): used for stat tiles — if admin wipes the number, hide the tile entirely.
  const showStat = (key, fallback) => {
    const v = T(key, fallback);
    return v != null && String(v).trim() !== "";
  };
  // Tile bodies may contain HTML (eg `<br/><span class="text-mango">…</span>`)
  // so we render through dangerouslySetInnerHTML. Source is admin-only and
  // entered via the Site Content CMS — there's no patron-supplied content here.
  const RT = (k, fallback) => {
    const val = T(k, fallback);
    return <span dangerouslySetInnerHTML={{ __html: val }} />;
  };
  const minThreshold = parseInt(c.homepage_charts_min_waves || "10", 10) || 10;
  const waitlistOpen = (c.waitlist_open || "true").toLowerCase() !== "false";
  const showCharts = insights.length >= minThreshold;
  // Admin-customizable hero image + theme (Site Content → Homepage — hero & stat
  // tiles). "light" auto-flips overline/title/subtitle/CTA/stat colours to dark
  // + adds a white scrim behind the image so text stays legible over a bright photo.
  const heroImg = c.home_hero_image_url || HERO_BG;
  const isLightHero = (c.home_hero_theme || "dark") === "light";

  return (
    <div className="min-h-screen bg-white themable-public">
      <SeoHead
        title={c.seo_home_title || "oodel. — Pakistan's Market Intelligence Platform"}
        description={c.seo_home_description || "Pakistan's first nationally representative consumer-intelligence panel. Live trackers, monthly snapshots, and continuous surveys for brands, agencies, and researchers."}
        canonical={`${(c.seo_canonical_base || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "")}/`}
        image={c.seo_default_image_url || undefined}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": c.brand_name || "oodel.",
          "url": c.seo_canonical_base || (typeof window !== "undefined" ? window.location.origin : "https://www.oodel.co"),
          "logo": c.seo_default_image_url || undefined,
          "sameAs": [c.seo_linkedin_url, c.seo_twitter_url].filter(Boolean),
          "description": c.seo_home_description || "Pakistan's market-intelligence panel.",
        }}
      />
      <PublicNav />

      {/* Hero — image + theme are admin-editable (Site Content → Homepage).
          Dark theme (default) = current look (near-black bg, white text, luminosity
          blend). Light theme = white bg, dark text, full-opacity image + a white
          gradient scrim on the text side so a bright photo never washes out copy. */}
      <section className={`border-b border-grid relative overflow-hidden ${isLightHero ? "bg-white text-neutral-900" : "bg-[#0A0A0A] text-white"}`} data-testid="hero-section" data-hero-theme={isLightHero ? "light" : "dark"}>
        <div
          className={`absolute inset-0 ${isLightHero ? "opacity-100" : "opacity-30 mix-blend-luminosity"}`}
          style={{ backgroundImage: `url(${heroImg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        {isLightHero && <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/45" />}
        <div className="relative max-w-[1400px] mx-auto px-6 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="overline text-mango mb-6" data-testid="hero-overline">{T("hero_overline", "PAKISTAN'S MARKET INTELLIGENCE PLATFORM")}</div>
            <h1 className={`text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] ${isLightHero ? "text-neutral-900" : "text-white"}`} data-testid="hero-title">
              {T("hero_title_lead", "The pulse of")} <span className="text-mango">{T("hero_title_emphasis", "230 million")}</span> {T("hero_title_trail", "people, charted in real time.")}
            </h1>
            <p className={`mt-8 text-lg max-w-2xl ${isLightHero ? "text-neutral-600" : "text-neutral-300"}`}>
              {T("hero_subtitle", "oodel. runs continuous representative surveys across Pakistan. We track political approval, economic confidence, brand reputation, and social attitudes — every wave, every week.")}
            </p>
            <div className="mt-10 flex gap-4">
              {(c.hero_cta_primary_enabled !== "false") && (
                <Link to="/trackers"><button data-testid="hero-cta-trackers" className={`bg-mango text-white px-6 py-3 font-semibold border-2 transition-colors ${isLightHero ? "border-neutral-900 hover:bg-neutral-900" : "border-white hover:bg-white hover:text-black"}`}>{T("hero_cta_primary", "Explore Trackers →")}</button></Link>
              )}
              {(c.hero_cta_secondary_enabled !== "false") && (
                <Link to="/register"><button data-testid="hero-cta-join" className={`px-6 py-3 font-semibold transition-colors ${isLightHero ? "border border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white" : "border border-white text-white hover:bg-white hover:text-black"}`}>{T("hero_cta_secondary", "Join the Panel")}</button></Link>
              )}
            </div>
          </div>
          <div className="md:col-span-4 hidden md:flex items-end">
            <div className={`border-l pl-6 space-y-6 w-full ${isLightHero ? "border-neutral-300" : "border-neutral-700"}`}>
              {showStat("stat_panelists", "48,217") && <Stat label={T("stat_panelists_label", "ACTIVE PANELISTS")} value={T("stat_panelists", "48,217")} />}
              {showStat("stat_waves", "312") && <Stat label={T("stat_waves_label", "WAVES PUBLISHED")} value={T("stat_waves", "312")} />}
              {showStat("stat_datapoints", "1.2M") && <Stat label={T("stat_datapoints_label", "DATA POINTS / WK")} value={T("stat_datapoints", "1.2M")} />}
            </div>
          </div>
        </div>
      </section>

      {/* Founding Patron CTA — replaces the legacy time-series preview strip.
          Hidden when admin closes the waitlist (site_content.waitlist_open=false). */}
      {waitlistOpen && <FoundingPatronCTA />}

      {/* Featured insights — only when we have at least homepage_charts_min_waves
          worth of closed public waves (default 10, admin-tunable). Hidden entirely
          when admin flips home_what_pakistan_saying_enabled = false. */}
      {showCharts && c.home_what_pakistan_saying_enabled !== "false" && (
      <section className="max-w-[1400px] mx-auto px-6 py-16" data-testid="home-insights">
        <div className="flex items-end justify-between mb-10 border-b border-black pb-4">
          <div>
            <div className="overline mb-2">{T("home_insights_overline", "SECTION — LATEST INSIGHTS")}</div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">{T("home_insights_heading", "What Pakistan is saying")}</h2>
          </div>
          <Link to="/insights" className="text-sm font-mono uppercase tracking-widest hover:text-mango flex items-center gap-1" data-testid="view-all-insights"><ArrowUpRight /> View all</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {insights.slice(0, 10).map((it) => (
            <Link key={`${it.kind}-${it.id}`} to={it.kind === "tracker" ? `/trackers/${it.id}` : `/snapshots/${it.id}`}
                  className="group border border-neutral-200 hover:border-black bg-white p-6 block transition-all" data-testid={`home-card-${it.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-neutral-500">{it.sector || "—"}</div>
                <div className="overline text-mango">{it.kind === "tracker" ? "TRACKER" : "SNAPSHOT"}</div>
              </div>
              <h3 className="text-lg font-bold tracking-tight mb-3 group-hover:text-mango transition-colors">{it.name}</h3>
              <div className="text-xs text-neutral-500">n = {it.n_sample || "?"} · {it.latest_date}</div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* Methodology strip */}
      <section className="border-y border-grid bg-[#F4F4F5] py-16">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="overline mb-3 text-mango">M01</div>
            <h3 className="text-2xl font-bold mb-2">{RT("method_m01_title", "Stratified quotas")}</h3>
            <p className="text-neutral-600">{RT("method_m01_body", "Every wave enforces gender, age, regional, and SEC quotas. Out-of-quota respondents are screened and the data is post-stratified.")}</p>
          </div>
          <div>
            <div className="overline mb-3 text-mango">M02</div>
            <h3 className="text-2xl font-bold mb-2">{RT("method_m02_title", "Verified panel")}</h3>
            <p className="text-neutral-600">{RT("method_m02_body", "Citizens self-register with mobile-verified profiles. Identity, demographics and consistency are validated continuously.")}</p>
          </div>
          <div>
            <div className="overline mb-3 text-mango">M03</div>
            <h3 className="text-2xl font-bold mb-2">{RT("method_m03_title", "Open methodology")}</h3>
            <p className="text-neutral-600">{RT("method_m03_body", "Sample size, fielding window and demographic breakdowns are visible alongside every tracker dot.")}</p>
          </div>
        </div>
      </section>

      {/* "Open Waves" strip — 3 latest open waves, admin-toggleable via
          Site Content. */}
      {c.home_waves_and_articles_enabled !== "false" && homeWaves.length > 0 && (
      <section className="max-w-[1400px] mx-auto px-6 py-16" data-testid="home-waves-articles">
        <div className="flex items-end justify-between mb-10 border-b border-black pb-4">
          <div>
            <div className="overline mb-2">{T("home_waves_overline", "SECTION — TAKE PART")}</div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">{T("home_waves_heading", "Open Waves")}</h2>
          </div>
          <Link to="/waves" className="text-sm font-mono uppercase tracking-widest hover:text-mango flex items-center gap-1" data-testid="view-all-waves-home"><ArrowUpRight /> View all waves</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-waves-articles-grid">
          {homeWaves.map((w) => <HomeWaveCard key={w.id} w={w} onOpen={openWave} />)}
        </div>
      </section>
      )}

      <footer className="border-t border-grid bg-[#0A0A0A] text-white py-12">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <span className="font-bold text-lg text-white" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span style={{ color: '#16A34A' }}>.</span></span>
            <div className="text-neutral-400 text-sm mt-2" data-testid="footer-tagline">{T("footer_tagline", "Independent Actionable Intelligence | Pakistan")}</div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3">
            <div className="overline text-neutral-500" data-testid="footer-copyright">{T("footer_copyright", "© 2026 oodel Research Pvt Ltd.")}</div>
            <div className="flex items-center gap-4 text-[11px] text-neutral-500">
              {(c.legal_privacy_enabled !== "false") && (
                <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy notice</Link>
              )}
              {(c.legal_privacy_enabled !== "false") && (c.legal_terms_enabled !== "false") && (
                <span className="text-neutral-700">·</span>
              )}
              {(c.legal_terms_enabled !== "false") && (
                <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
              )}
            </div>
            <AppStoreButtons />
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * AppStoreButtons — Apple App Store + Google Play official badges.
 * Sits inside the right-hand column of the footer (under the copyright line).
 *
 * Admin controls (in /admin/site-content → "App Store Buttons (footer)"):
 *   - app_button_ios_enabled      toggle  (off → hide that badge)
 *   - app_button_ios_url          URL     (blank → badge shown but not clickable)
 *   - app_button_android_enabled  toggle  (off → hide that badge)
 *   - app_button_android_url      URL     (blank → badge shown but not clickable)
 *
 * If both toggles are off, the whole row collapses.
 */
function AppStoreButtons() {
  const c = useSiteContent();
  // Hide the entire row when running inside the native app — downloading "the app"
  // from inside the app is meaningless and would confuse users.
  if (isNativeApp()) return null;
  const iosOn = String(c.app_button_ios_enabled || "").toLowerCase() === "true";
  const androidOn = String(c.app_button_android_enabled || "").toLowerCase() === "true";
  if (!iosOn && !androidOn) return null;
  const iosUrl = String(c.app_button_ios_url || "").trim();
  const androidUrl = String(c.app_button_android_url || "").trim();
  return (
    <div className="flex items-center gap-3" data-testid="footer-app-buttons">
      {iosOn && (
        <StoreBadge
          href={iosUrl}
          testid="app-button-ios"
          alt="Download on the App Store"
          src="/badges/app-store-badge.svg"
        />
      )}
      {androidOn && (
        <StoreBadge
          href={androidUrl}
          testid="app-button-android"
          alt="Get it on Google Play"
          src="/badges/google-play-badge.png"
        />
      )}
    </div>
  );
}

function StoreBadge({ href, testid, alt, src }) {
  // Official store badges have intrinsic aspect ratio; we control size via height only.
  // 38px ≈ standard Apple/Google "small footer" badge sizing — sits cleanly under the copyright line.
  const img = <img src={src} alt={alt} className="h-10 w-auto" />;
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} aria-label={alt} className="inline-block hover:opacity-80 transition-opacity">
        {img}
      </a>
    );
  }
  return (
    <div data-testid={`${testid}-disabled`} className="opacity-60 cursor-not-allowed" title="Admin hasn't set the link yet">
      {img}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="overline text-neutral-500">{label}</div>
      <div className="font-mono text-3xl font-bold text-mango">{value}</div>
    </div>
  );
}

// ITER 123 — homepage "take part" strip cards. Same visual language and
// click-gating as /waves and /articles (public "browse before you sign up"
// surfaces) — this is just a 2+1 preview of those same feeds.
function HomeWaveCard({ w, onOpen }) {
  const daysLeft = w.end_date ? Math.ceil((new Date(w.end_date) - new Date()) / 86400000) : null;
  return (
    <button
      onClick={() => onOpen(w)}
      data-testid={`home-wave-${w.id}`}
      className="text-left bg-white border border-neutral-200 rounded-2xl shadow-sm hover:shadow-md hover:border-mango/40 hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col group"
    >
      <div className="aspect-[4/5] bg-emerald-50 overflow-hidden flex items-center justify-center">
        {w.cover_image ? (
          <img src={w.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <ListChecks size={28} className="text-mango/40" />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        {w.is_featured && (
          <span className="self-start mb-2 text-[10px] font-bold uppercase tracking-wider bg-mango/10 text-mango px-2 py-0.5 rounded-full">Featured</span>
        )}
        {w.sector_key && <div className="text-[10px] uppercase tracking-wide font-mono text-neutral-400 mb-1">{w.sector_key}</div>}
        <h3 className="text-base font-bold leading-snug text-neutral-900 group-hover:text-mango transition-colors line-clamp-2">{w.name}</h3>
        {w.intro_text && <p className="text-xs text-neutral-500 mt-1.5 line-clamp-2">{w.intro_text}</p>}
        <div className="mt-auto pt-3">
          <div className="pt-3 border-t border-neutral-100 flex items-center justify-between text-xs text-neutral-500">
            <span className="flex items-center gap-1.5"><ListChecks size={14} /> {w.question_count} questions</span>
            {daysLeft !== null && daysLeft >= 0 && (
              <span className="flex items-center gap-1 text-red-600 font-medium"><ClockCountdown size={13} weight="bold" /> {daysLeft === 0 ? "closes today" : `${daysLeft}d`}</span>
            )}
          </div>
          <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-mango">
            Sign up to take this survey <ArrowRight size={13} />
          </div>
        </div>
      </div>
    </button>
  );
}



function TrackerCard({ t }) {
  // Build mini sparkline from latest results overall_pct sum
  const colors = ["#023B24", "#16A34A", "#0055FF", "#F4C430", "#8B5CF6"];
  return (
    <Link to={`/trackers/${t.id}`} data-testid={`tracker-card-${t.id}`} className="group border border-neutral-200 hover:border-black bg-white p-6 block transition-all">
      <div className="overline mb-2 text-neutral-500">{t.category}</div>
      <h3 className="text-xl font-bold tracking-tight mb-4 group-hover:text-mango transition-colors">{t.name}</h3>
      <div className="h-24 -mx-2">
        <Sparkline trackerId={t.id} colors={colors} />
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-grid pt-3">
        <span className="overline">VIEW TRACKER</span>
        <ArrowUpRight size={18} className="text-mango" />
      </div>
    </Link>
  );
}

function Sparkline({ trackerId, colors }) {
  const [data, setData] = useState([]);
  const [keys, setKeys] = useState([]);
  useEffect(() => {
    api.get(`/trackers/${trackerId}/data`).then((r) => {
      setData(r.data.series || []);
      setKeys((r.data.options || []).map((o) => o.key));
    });
  }, [trackerId]);
  if (!data.length) return <div className="h-full bg-neutral-50" />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data}>
        <XAxis dataKey="date" hide />
        <YAxis hide domain={[0, 'dataMax + 5']} />
        {keys.slice(0, 4).map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={colors[i % colors.length]} strokeWidth={2} dot={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
